import { useState } from "react";
import { motion } from "framer-motion";
import { Send, ArrowRight } from "lucide-react";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    use_case: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;

    setStatus("sending");

    const subject = encodeURIComponent(`LeapForce demo request - ${form.company}`);
    const body = encodeURIComponent(
      [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Company: ${form.company}`,
        `Use Case: ${form.use_case || "Not provided"}`,
      ].join("\n")
    );

    window.location.href = `mailto:accounts@bigleap.app?subject=${subject}&body=${body}`;
    setStatus("success");
    setForm({ name: "", email: "", company: "", use_case: "" });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-[#050508]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#1A2332] overflow-hidden">
          {/* Left: CTA */}
          <div className="relative bg-[#0D0F14] p-10 lg:p-16 flex flex-col justify-center overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-20" />
            <div className="relative z-10">
              <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-[0.2em] flex items-center gap-2 mb-6">
                <ArrowRight size={14} />
                Get Started
              </span>
              <h2
                data-testid="contact-headline"
                className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 leading-[1.1]"
              >
                Secure Your Agents
                <br />
                <span className="text-[#00E5FF]">Before They Ship</span>
              </h2>
              <p className="text-base text-[#94A3B8] leading-relaxed mb-8 max-w-md">
                Book a demo to see LeapForce analyze your agent workflows 
                in real-time. Deploy the action chain intelligence layer 
                your enterprise stack is missing.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { val: "<1ms", label: "Inference" },
                  { val: "99%+", label: "Accuracy" },
                  { val: "Zero", label: "LLM Deps" },
                ].map((stat) => (
                  <div key={stat.label} className="border border-[#1A2332] p-3">
                    <div className="text-xl font-bold text-[#00E5FF] font-mono">
                      {stat.val}
                    </div>
                    <div className="text-[10px] font-mono text-[#475569] uppercase tracking-[0.15em]">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-[#050508] p-10 lg:p-16 border-l border-[#1A2332]">
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">
                Book a Demo
              </h3>
              <p className="text-sm text-[#94A3B8]">
                Fill in your details to draft an email to the Big Leap team.
              </p>
            </div>

            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="border border-[#10B981]/30 bg-[#10B981]/5 p-8 text-center"
              >
                <div className="w-12 h-12 border border-[#10B981] flex items-center justify-center mx-auto mb-4">
                  <Send size={20} className="text-[#10B981]" />
                </div>
                <p className="text-white font-semibold mb-1">Request Received</p>
                <p className="text-sm text-[#94A3B8]">
                  Your email draft is ready to send.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label className="text-xs font-mono text-[#94A3B8] uppercase tracking-wider block mb-2">
                    Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    data-testid="contact-name-input"
                    required
                    className="w-full bg-[#050508] border border-[#1A2332] text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] px-4 py-3 text-sm outline-none transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-[#94A3B8] uppercase tracking-wider block mb-2">
                    Work Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid="contact-email-input"
                    required
                    className="w-full bg-[#050508] border border-[#1A2332] text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] px-4 py-3 text-sm outline-none transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-[#94A3B8] uppercase tracking-wider block mb-2">
                    Company
                  </label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    data-testid="contact-company-input"
                    required
                    className="w-full bg-[#050508] border border-[#1A2332] text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] px-4 py-3 text-sm outline-none transition-colors"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-[#94A3B8] uppercase tracking-wider block mb-2">
                    Use Case
                  </label>
                  <textarea
                    name="use_case"
                    value={form.use_case}
                    onChange={handleChange}
                    data-testid="contact-usecase-input"
                    rows={3}
                    className="w-full bg-[#050508] border border-[#1A2332] text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] px-4 py-3 text-sm outline-none transition-colors resize-none"
                    placeholder="Briefly describe your agent workflows..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={status === "sending"}
                  className="w-full bg-[#00E5FF] text-[#050508] font-bold py-4 hover:bg-[#33EAFF] transition-colors glow-cyan hover:glow-cyan-strong text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {status === "sending" ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send size={14} />
                      Book Demo
                    </>
                  )}
                </button>
                {status === "error" && (
                  <p
                    data-testid="contact-error"
                    className="text-xs text-[#EF4444] font-mono"
                  >
                    Something went wrong. Please email accounts@bigleap.app.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
